import { LayoutGrid, Star, Inbox } from "lucide-react";

export const ALL = "__all__";
export const FAVORITES = "__favorites__";
export const UNCATEGORIZED = "__uncategorized__";

/**
 * Category filter. `variant="rail"` is the sticky left column (desktop only);
 * `variant="chips"` is the horizontally scrollable row shown below lg.
 */
export default function CategorySidebar({ categories, apps, selected, onSelect, variant = "rail" }) {
    const counts = apps.reduce(
        (acc, a) => {
            acc[ALL] += 1;
            if (a.favorite) acc[FAVORITES] += 1;
            const key = a.category_id || UNCATEGORIZED;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        },
        { [ALL]: 0, [FAVORITES]: 0 },
    );

    const items = [
        { key: ALL, label: "All apps", icon: LayoutGrid },
        { key: FAVORITES, label: "Favorites", icon: Star },
        ...categories.map((c) => ({ key: c.id, label: c.name, emoji: c.icon, color: c.color })),
        { key: UNCATEGORIZED, label: "Uncategorized", icon: Inbox },
    ];

    const renderItem = (item) => {
        const active = selected === item.key;
        const count = counts[item.key] || 0;
        const Icon = item.icon;
        const base =
            variant === "rail"
                ? "w-full px-3 py-2 rounded-xl text-sm flex items-center gap-2.5"
                : "shrink-0 px-3 py-1.5 rounded-full text-sm flex items-center gap-2 whitespace-nowrap";
        return (
            <button
                key={item.key}
                type="button"
                onClick={() => onSelect(item.key)}
                data-testid={`category-filter-${item.key}`}
                aria-current={active ? "true" : undefined}
                className={`${base} font-medium transition-colors ${
                    active
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                }`}
            >
                {item.emoji ? (
                    <span className="text-base leading-none">{item.emoji}</span>
                ) : (
                    <Icon className="h-4 w-4 shrink-0" />
                )}
                <span className="truncate">{item.label}</span>
                <span
                    className={`ml-auto text-xs tabular-nums ${
                        active ? "opacity-70" : "opacity-60"
                    }`}
                >
                    {count}
                </span>
            </button>
        );
    };

    if (variant === "chips") {
        return (
            <div
                className="lg:hidden flex gap-2 overflow-x-auto pb-2 -mx-1 px-1"
                data-testid="category-chips"
            >
                {items.map(renderItem)}
            </div>
        );
    }

    return (
        <aside className="hidden lg:block w-56 shrink-0" data-testid="category-sidebar">
            <div className="sticky top-24 glass-tile rounded-3xl p-3">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold px-3 pt-2 pb-3">
                    Categories
                </p>
                <nav className="flex flex-col gap-0.5 max-h-[calc(100vh-14rem)] overflow-y-auto">
                    {items.map(renderItem)}
                </nav>
            </div>
        </aside>
    );
}
